import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { refreshToken, isTokenExpired, isTokenExpiringSoon } from '../services/authService';
import {
  clearAuthData,
  decodeAccessToken,
  getAccessToken,
  normalizeToken,
  persistAuthTokens,
} from '../utils/auth';

const VALID_ROLES = ['Admin', 'SuperAgent', 'Agent'];

export const useAuth = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const attemptTokenRefresh = async (): Promise<boolean> => {
    const refresh = normalizeToken(localStorage.getItem('refreshToken'));
    const username = localStorage.getItem('username');
    if (!refresh || !username) return false;
    try {
      return persistAuthTokens(await refreshToken({ token: refresh, name: username }));
    } catch (e) {
      console.error('Token refresh failed');
      return false;
    }
  };

  const failAuth = () => {
    clearAuthData();
    setIsAuthenticated(false);
    navigate('/login');
  };

  const checkAuthStatus = async () => {
    const token = getAccessToken();
    if (!token) {
      failAuth();
      setIsLoading(false);
      return;
    }

    const decoded = decodeAccessToken();
    if (!decoded || !VALID_ROLES.includes(decoded['X-SC-Role'])) {
      failAuth();
      setIsLoading(false);
      return;
    }

    if (isTokenExpired(token)) {
      if (!(await attemptTokenRefresh()) || !decodeAccessToken()) {
        failAuth();
        setIsLoading(false);
        return;
      }
      setIsAuthenticated(true);
      setIsLoading(false);
      return;
    }

    const current = getAccessToken();
    if (current && isTokenExpiringSoon(current, 5)) {
      attemptTokenRefresh().catch(() => undefined);
    }

    setIsAuthenticated(true);
    setIsLoading(false);
  };

  useEffect(() => {
    checkAuthStatus();
  }, [navigate]);

  return { isAuthenticated, isLoading, checkAuthStatus, clearAuthData };
};
